import { memo, useCallback, useId, useMemo, useRef, useState } from 'react';
import { AddCircleOutline as AddCircleOutlineIcon, DragIndicator as DragIndicatorIcon, MoreHoriz as MoreHorizIcon, RemoveCircleOutline as RemoveCircleOutlineIcon } from '@mui/icons-material';
import { IconButton, InputBase, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronDown, ChevronUp } from 'react-feather';
import type { BlockItem, BlockWidgetItem, WidgetConfig } from '@trinity/chat/types/dashboard-config';
import { BLOCK_GAP, ROW_HEIGHT, SLOT_PADDING, getBlockRowHeightMap, getContentHeightUnits, getPreviewBlockRowHeightMap, getPreviewContentHeightUnits } from './dashboardLayoutUtils';
import {
  useDashboardBuilder,
  useDashboardBuilderEditing,
  useDashboardConfig,
  useWidgetRegistry,
} from '@trinity/chat-components/containers/Dashboard/context';
import { useDashboardData } from '@trinity/chat-components/containers/Dashboard/context';
import { SectionCardShell } from './SectionCardShell';
import { WidgetPalette } from './WidgetPalette';
import { ThemedMenu } from './shared-styled';

// ─── Types ────────────────────────────────────────────────────────────────────

type Slot = 1 | 2 | 3;

type Props = {
  blockId: string;
  title: string;
  /** Whether the block title is hidden in the rendered dashboard (shows in section-title style in the builder). */
  hideTitle: boolean;
  /** Number of grid columns this block spans (1, 2, or 3) */
  blockCols: 1 | 2 | 3;
  /** Whether this block is currently collapsed to header-only height. Controlled externally. */
  collapsed: boolean;
  /** Toggle the collapsed state (controlled by BuilderCanvas). */
  onToggleCollapse: () => void;
  /** When true, renders in clean read-only preview mode (no editing chrome). */
  readOnly?: boolean;
};

type DragData =
  | { kind: 'move'; blockId: string; row: number; slot: Slot }
  | { kind: 'resize'; blockId: string; row: number; slot: Slot }
  | { kind: 'section-move'; blockId: string; row: number; slot: Slot }
  | { kind: 'section-resize'; blockId: string; sectionId: string; row: number; slot: Slot }
  /** Widget dragged from within a section — enables section→block and section→section drops */
  | { kind: 'section-widget-move'; blockId: string; sectionId: string; row: number; slot: Slot };

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Maximum 1-indexed row used by any item in a block. Returns 1 if no items. */
function getMaxRow(items: BlockItem[]): number {
  if (items.length === 0) return 1;
  return Math.max(...items.map(item => item.row ?? 1));
}

/**
 * Returns true when all column-slots in the given row are fully covered.
 */
function isRowFull(items: BlockItem[], row: number, cols: number): boolean {
  const rowItems = items.filter(item => (item.row ?? 1) === row);
  let covered = 0;
  for (const item of rowItems) covered += item.span;
  return covered >= cols;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * WidgetCardShell — full-width block shell that renders a multi-row 3-column slot grid.
 *
 * Rows are created automatically: when all 3 columns in the last row are filled,
 * a new empty row is appended at the bottom.
 *
 * Users can:
 *   - Click any empty slot to open the widget palette and add a widget there
 *   - Drag a widget card to any slot in any row/block to re-position it
 *   - Drag the ↔ resize handle on the right edge of a widget to expand its column span
 *     (widgets that would be displaced are pushed to the next row rather than deleted)
 *   - Expand / shrink a widget's span via its actions menu
 *   - Configure a widget to open the sidebar
 */
export const WidgetCardShell = memo(function WidgetCardShell({
  blockId,
  title,
  hideTitle,
  blockCols,
  collapsed,
  onToggleCollapse,
  readOnly = false,
}: Props): React.ReactElement {
  const { config } = useDashboardConfig();
  const { registry } = useWidgetRegistry();
  const {
    removeBlock,
    updateBlockTitle,
    toggleBlockHideTitle,
    deleteEmptyRow,
    addSection,
    removeWidgetAtSlot,
    moveWidget,
    moveWidgetFromSectionToBlock,
    moveSection,
    updateWidgetSpan,
    updateWidgetRowSpan,
    updateSectionItemSpan,
  } = useDashboardBuilderEditing();
  const { setSelectedBlockId, setSidebarPanel, setSelectedWidgetId, setPendingSlot, setPendingWidgetType, selectedWidgetId, pendingSlot } = useDashboardBuilder();

  const block = config.blocks.find(b => b.id === blockId);
  const items = block?.items ?? [];

  // Build a height-map: Map<blockRowIndex, heightUnits>.
  // In preview/readOnly, use the preview variant which strips trailing empty rows
  // and minInnerRows padding from sections — those exist only for drag-drop in the builder.
  const heightMap = useMemo(
    () => readOnly ? getPreviewBlockRowHeightMap(items) : getBlockRowHeightMap(items),
    [items, readOnly],
  );

  // Total content height in row units (includes trailing empty row when last row is full).
  const contentHeight = readOnly ? getPreviewContentHeightUnits(items) : getContentHeightUnits(items);

  // Total block height respects user-set minRows (only in builder; preview shows exact content).
  const totalHeight = readOnly ? contentHeight : Math.max(block?.minRows ?? 1, contentHeight);

  // Extra flex units to distribute to the last rendered row (accounts for minRows padding).
  const extraFlex = totalHeight - contentHeight;

  // Trailing row calculation:
  // - Section at last row spans rows [maxRow .. maxRow+sectionRows-1], so the
  //   trailing empty row must come AFTER that span: maxRow + sectionRows.
  //   Using maxRow+1 (old) caused the trailing row to be absorbed into the
  //   section group, giving that group all the flex height → sub-rows too tall.
  // - Full widget row: +1 as before.
  // - Otherwise: last row is the trailing row itself.
  const maxRow = getMaxRow(items);
  const sectionAtMaxRow = items.find(
    (i): i is Extract<BlockItem, { type: 'section' }> =>
      (i.row ?? 1) === maxRow && i.type === 'section',
  ) ?? null;
  const lastRowSectionHeight = sectionAtMaxRow ? (heightMap.get(maxRow) ?? 1) : 0;
  // Multi-row widget at the last row (rowSpan > 1)?
  const multiRowWidgetAtMaxRow = items.find(
    (i): i is Extract<BlockItem, { type: 'widget' }> =>
      (i.row ?? 1) === maxRow && i.type === 'widget' && (i.rowSpan ?? 1) > 1,
  ) ?? null;
  const lastRowWidgetRowSpan = multiRowWidgetAtMaxRow?.rowSpan ?? 1;
  const trailingRowIdx =
    items.length === 0 ? 1
    : sectionAtMaxRow ? maxRow + lastRowSectionHeight          // after section span
    : multiRowWidgetAtMaxRow ? maxRow + lastRowWidgetRowSpan  // after multi-row widget span
    : isRowFull(items, maxRow, blockCols) ? maxRow + 1                   // widgets fill last row
    : maxRow;                                                  // partial widget row
  // Extra rows from block.minRows are appended as real empty rows so placeholders appear for drag-drop.
  const rows = Array.from({ length: trailingRowIdx + extraFlex }, (_, i) => i + 1); // [1, …, trailingRowIdx + extraFlex]

  // Group block rows for rendering.
  // A section OR a multi-row widget absorbs the next (N-1) successive block rows into its
  // CSS grid, giving each sub-row its own independent column of widget/placeholder slots.
  // This lets users place a 1×1 widget beside the top AND beside the bottom of a tall item.
  const renderedGroups = useMemo(() => {
    const groups: Array<{
      primaryRow: number;
      blockRows: number[];   // [primaryRow, …, primaryRow + groupRows - 1]
      flexSpan: number;      // flex weight for this group's shell container
      rowCount: number;      // CSS grid rows inside this group
      sectionItem: Extract<BlockItem, { type: 'section' }> | null;
      sectionRows: number;
    }> = [];
    const absorbed = new Set<number>();

    for (const row of rows) {
      if (absorbed.has(row)) continue;
      const si =
        items.find(
          (i): i is Extract<BlockItem, { type: 'section' }> =>
            (i.row ?? 1) === row && i.type === 'section',
        ) ?? null;
      const sectionRows = si ? (heightMap.get(row) ?? 1) : 1;
      // Multi-row span from any widget in this primary row (max rowSpan)
      const widgetRows = items
        .filter((i): i is Extract<BlockItem, { type: 'widget' }> => i.type === 'widget' && (i.row ?? 1) === row)
        .reduce((m, i) => Math.max(m, i.rowSpan ?? 1), 1);
      const groupRows = Math.max(sectionRows, widgetRows);

      const blockRows: number[] = [row];
      for (let k = 1; k < groupRows; k++) {
        absorbed.add(row + k);
        blockRows.push(row + k);
      }
      groups.push({ primaryRow: row, blockRows, flexSpan: groupRows, rowCount: groupRows, sectionItem: si, sectionRows });
    }
    return groups;
  }, [rows, items, heightMap]);

  // ── Title ──────────────────────────────────────────────────────────────────
  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => updateBlockTitle(blockId, e.target.value),
    [updateBlockTitle, blockId],
  );

  // ── Block menu ─────────────────────────────────────────────────────────────
  const blockMenuId = useId();
  const [blockMenuAnchor, setBlockMenuAnchor] = useState<null | HTMLElement>(null);

  // ── Per-widget menu ────────────────────────────────────────────────────────
  const widgetMenuId = useId();
  const [widgetMenu, setWidgetMenu] = useState<{ anchorEl: HTMLElement; row: number; slot: Slot } | null>(null);
  const widgetMenuBw = widgetMenu
    ? (items.find((item): item is BlockWidgetItem => item.type === 'widget' && item.slot === widgetMenu.slot && (item.row ?? 1) === widgetMenu.row) ?? null)
    : null;

  // collapsed / onToggleCollapse are received as props — BuilderCanvas controls
  // the actual RGL item height so it shrinks/expands in the layout.

  // ── Palette ────────────────────────────────────────────────────────────────
  const [paletteTarget, setPaletteTarget] = useState<{ row: number; slot: Slot } | null>(null);

  // Track whether the block-level primary "Add Widget" placeholder has been assigned.
  // Declared here so it survives across the renderedGroups.map callbacks, which execute
  // synchronously during render — safe to use a mutable variable.
  let blockPrimaryAssigned = false;

  // Track which empty group is being hovered for delete — drives the blue tint
  const [deleteHoverRow, setDeleteHoverRow] = useState<number | null>(null);

  // ── Drag & drop ────────────────────────────────────────────────────────────
  const [dragOverTarget, setDragOverTarget] = useState<{ row: number; slot: Slot } | null>(null);

  const handleMoveDragStart = useCallback(
    (e: React.DragEvent, row: number, slot: Slot) => {
      const data: DragData = { kind: 'move', blockId, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
      e.stopPropagation();
    },
    [blockId],
  );

  const handleResizeDragStart = useCallback(
    (e: React.DragEvent, row: number, slot: Slot) => {
      e.stopPropagation();
      const data: DragData = { kind: 'resize', blockId, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
    },
    [blockId],
  );

  const handleSectionResizeDragStart = useCallback(
    (e: React.DragEvent, sectionId: string, row: number, slot: Slot) => {
      e.stopPropagation();
      const data: DragData = { kind: 'section-resize', blockId, sectionId, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
    },
    [blockId],
  );

  const handleDragOver = useCallback((e: React.DragEvent, row: number, slot: Slot) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ row, slot });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toRow: number, toSlot: Slot) => {
      e.preventDefault();
      setDragOverTarget(null);
      try {
        const data = JSON.parse(e.dataTransfer.getData('text/plain')) as DragData;
        if (data.kind === 'move') {
          moveWidget(data.blockId, data.row, data.slot, blockId, toRow, toSlot);
        } else if (data.kind === 'section-widget-move') {
          // Widget dragged from inside a section — drop it into this block slot
          moveWidgetFromSectionToBlock(data.blockId, data.sectionId, data.row, data.slot, blockId, toRow, toSlot);
        } else if (data.kind === 'section-move') {
          if (data.blockId === blockId) {
            moveSection(blockId, data.row, data.slot, toRow, toSlot);
          }
        } else if (data.kind === 'section-resize') {
          if (data.blockId === blockId && data.row === toRow && toSlot >= data.slot) {
            const newSpan = Math.min(3, Math.max(1, toSlot - data.slot + 1)) as Slot;
            updateSectionItemSpan(blockId, data.sectionId, newSpan);
          }
        } else if (data.kind === 'resize') {
          // Expand (drop to the right) or shrink (drop on own slot or within span)
          if (data.blockId === blockId && data.row === toRow && toSlot >= data.slot) {
            const newSpan = Math.min(3, Math.max(1, toSlot - data.slot + 1)) as Slot;
            updateWidgetSpan(blockId, data.row, data.slot, newSpan);
          }
        }
      } catch { /* ignore malformed drag data */ }
    },
    [blockId, moveWidget, moveWidgetFromSectionToBlock, moveSection, updateWidgetSpan, updateSectionItemSpan],
  );

  const handleConfigure = useCallback(
    (row: number, slot: Slot) => {
      const foundItem = items.find((item): item is BlockWidgetItem => item.type === 'widget' && item.slot === slot && (item.row ?? 1) === row);
      if (!foundItem) return;
      setSelectedBlockId(blockId);
      setSelectedWidgetId(foundItem.widget.id);
      setSidebarPanel('chart-config');
      setWidgetMenu(null);
    },
    [items, blockId, setSelectedBlockId, setSelectedWidgetId, setSidebarPanel],
  );

  return (
    <Shell>
      {/* ─── Block header ─── */}
      {readOnly ? (
        // Preview: show the block title as plain text with no editing chrome.
        // Hidden when the user has toggled "hide title" or there is no title.
        (!hideTitle && title) ? (
          <PreviewBlockHeader>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {title}
            </Typography>
          </PreviewBlockHeader>
        ) : null
      ) : (
        <ShellHeader className="drag-handle" collapsed={collapsed} hideTitle={hideTitle}>
          <TitleInput
            hideTitle={hideTitle}
            value={title}
            onChange={handleTitleChange}
            placeholder="Block name..."
            inputProps={{ 'aria-label': 'Block title' }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
          />
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); setBlockMenuAnchor(e.currentTarget); }}
            onMouseDown={e => e.stopPropagation()}
            aria-label="Block actions"
            aria-controls={blockMenuAnchor ? blockMenuId : undefined}
            aria-haspopup="true"
            sx={{ flexShrink: 0, padding: hideTitle ? '1px' : undefined }}
          >
            <MoreHorizIcon fontSize={hideTitle ? 'inherit' : 'small'} />
          </IconButton>
          <ThemedMenu
            id={blockMenuId}
            anchorEl={blockMenuAnchor}
            open={Boolean(blockMenuAnchor)}
            onClose={() => setBlockMenuAnchor(null)}
            MenuListProps={{ dense: true }}
          >
            <MenuItem
              onClick={() => { setBlockMenuAnchor(null); toggleBlockHideTitle(blockId); }}
            >
              {hideTitle ? 'Show title' : 'Hide title'}
            </MenuItem>
            <MenuItem
              onClick={() => { setBlockMenuAnchor(null); removeBlock(blockId); }}
              sx={{ color: 'error.main' }}
            >
              Delete block
            </MenuItem>
          </ThemedMenu>
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onToggleCollapse(); }}
            onMouseDown={e => e.stopPropagation()}
            aria-label={collapsed ? 'Expand block' : 'Collapse block'}
            sx={{ flexShrink: 0, ml: 0.25, padding: hideTitle ? '1px' : undefined }}
          >
            {collapsed
              ? <ChevronDown size={hideTitle ? 12 : 16} />
              : <ChevronUp size={hideTitle ? 12 : 16} />}
          </IconButton>
        </ShellHeader>
      )}

      {/* ─── Rows ─── */}
      {!collapsed && renderedGroups.map(({ primaryRow, blockRows, flexSpan, rowCount, sectionItem, sectionRows }) => {
        // Columns physically covered by the section across every sub-row
        const secCols = new Set<number>();
        if (sectionItem) {
          for (let c = 0; c < sectionItem.span; c++) secCols.add((sectionItem.slot as number) + c);
        }

        // A group is empty when it has no section and no items in any of its block rows
        const isGroupEmpty = !sectionItem && blockRows.every(
          blockRow => items.filter(i => (i.row ?? 1) === blockRow).length === 0,
        );

        // In read-only (renderer) mode, skip empty rows entirely — no placeholders to show.
        if (readOnly && isGroupEmpty) return null;

        return (
          <SlotGrid key={primaryRow} flexSpan={flexSpan} rowCount={rowCount} cols={blockCols} readOnly={readOnly} deleteHovering={isGroupEmpty && deleteHoverRow === primaryRow}>
            {/* Section spans all sub-rows via explicit CSS grid placement */}
            {sectionItem && (() => {
              const sSlot = sectionItem.slot as number;
              return (
                <SectionSlotCell
                  key={`section-${primaryRow}-${sSlot}`}
                  span={sectionItem.span}
                  style={{ gridColumn: `${sSlot} / span ${sectionItem.span}`, gridRow: `1 / span ${sectionRows}` }}
                  {...(!readOnly && {
                    onDragOver: (e: React.DragEvent) => handleDragOver(e, primaryRow, sSlot as Slot),
                    onDragLeave: () => setDragOverTarget(null),
                    onDrop: (e: React.DragEvent) => handleDrop(e, primaryRow, sSlot as Slot),
                  })}
                >
                  <SectionCardShell
                    blockId={blockId}
                    section={sectionItem.section}
                    sectionItemSpan={sectionItem.span}
                    sectionItemRow={primaryRow}
                    sectionItemSlot={sSlot as Slot}
                    readOnly={readOnly}
                  />
                  {!readOnly && (
                    <SectionResizeEdge
                      draggable
                      onDragStart={e => handleSectionResizeDragStart(e, sectionItem.section.id, primaryRow, sSlot as Slot)}
                      onMouseDown={e => e.stopPropagation()}
                    />
                  )}
                </SectionSlotCell>
              );
            })()}

            {/* Each sub-row gets independent widget/placeholder cells */}
            {blockRows.flatMap((blockRow, subRowIdx) => {
              const cssRow = subRowIdx + 1;
              const rowNonSectionItems = items.filter(i => (i.row ?? 1) === blockRow && i.type !== 'section');

              // Columns occupied in this sub-row
              const coveredCols = new Set<number>(secCols);
              for (const item of rowNonSectionItems) {
                for (let c = 0; c < item.span; c++) coveredCols.add((item.slot as number) + c);
              }
              // In absorbed sub-rows (subRowIdx > 0), also mark columns covered by
              // multi-row widgets from the primary row that extend through this sub-row.
              if (subRowIdx > 0) {
                for (const pItem of items) {
                  if (pItem.type !== 'widget' || (pItem.row ?? 1) !== primaryRow) continue;
                  const pRowSpan = pItem.rowSpan ?? 1;
                  if (subRowIdx < pRowSpan) {
                    for (let c = pItem.slot as number; c < (pItem.slot as number) + pItem.span; c++) {
                      coveredCols.add(c);
                    }
                  }
                }
              }

              const cells: React.ReactNode[] = [];

              // Placed widgets
              for (const item of rowNonSectionItems) {
                if (item.type !== 'widget') continue;
                const iSlot = item.slot as number;
                const itemRowSpan = item.rowSpan ?? 1;
                const widgetEntry = registry.get(item.widget.type);
                cells.push(
                  <WidgetSlotCell
                    key={`${blockRow}-${iSlot}`}
                    span={item.span}
                    readOnly={readOnly}
                    isEditing={!readOnly && selectedWidgetId === item.widget.id}
                    noBorder={widgetEntry?.noBorder ?? false}
                    {...(!readOnly && {
                      draggable: true,
                      onDragStart: (e: React.DragEvent) => handleMoveDragStart(e, blockRow, iSlot as Slot),
                      onDragOver: (e: React.DragEvent) => handleDragOver(e, blockRow, iSlot as Slot),
                      onDragLeave: () => setDragOverTarget(null),
                      onDrop: (e: React.DragEvent) => handleDrop(e, blockRow, iSlot as Slot),
                    })}
                    isDragOver={!readOnly && dragOverTarget?.row === blockRow && dragOverTarget.slot === iSlot}
                    style={{ gridColumn: `${iSlot} / span ${item.span}`, gridRow: `${cssRow} / span ${itemRowSpan}` }}
                  >
                    {!readOnly && (
                      <SlotWidgetHeader>
                        <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'grab', flexShrink: 0 }} />
                        <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.widget.config.title || item.widget.type}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); setWidgetMenu({ anchorEl: e.currentTarget, row: blockRow, slot: iSlot as Slot }); }}
                          onMouseDown={e => e.stopPropagation()}
                          sx={{ flexShrink: 0, p: 0.25 }}
                        >
                          <MoreHorizIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </SlotWidgetHeader>
                    )}
                    <SlotContent>
                      <WidgetRenderer widgetId={item.widget.id} config={item.widget} width={item.span} height={itemRowSpan} />
                    </SlotContent>
                    {!readOnly && (
                      <>
                        <ResizeEdge
                          draggable
                          onDragStart={e => handleResizeDragStart(e, blockRow, iSlot as Slot)}
                          onMouseDown={e => e.stopPropagation()}
                        />
                        <WidgetRowResizeHandle
                          rowSpan={itemRowSpan}
                          onResize={newRowSpan => updateWidgetRowSpan(blockId, blockRow, iSlot as Slot, newRowSpan as 1 | 2 | 3)}
                        />
                      </>
                    )}
                  </WidgetSlotCell>,
                );
              }

              // Free-column placeholders — only rendered in edit mode.
              // All others are inert drop targets only.
              if (!readOnly) {
                for (let col = 1; col <= blockCols; col++) {
                  if (coveredCols.has(col)) continue;
                  const isPrimaryEmpty = !blockPrimaryAssigned;
                  blockPrimaryAssigned = true;
                  cells.push(
                    <SlotPlaceholder
                      key={`${blockRow}-${col}`}
                      isDragOver={dragOverTarget?.row === blockRow && dragOverTarget.slot === col}
                      isPrimary={isPrimaryEmpty}
                      isPendingTarget={pendingSlot?.blockId === blockId && pendingSlot?.row === blockRow && pendingSlot?.slot === col}
                      onDragOver={e => handleDragOver(e, blockRow, col as Slot)}
                      onDragLeave={() => setDragOverTarget(null)}
                      onDrop={e => handleDrop(e, blockRow, col as Slot)}
                      onClick={isPrimaryEmpty ? () => setPaletteTarget({ row: blockRow, slot: col as Slot }) : undefined}
                      style={{ gridColumn: col, gridRow: cssRow }}
                    >
                      {isPrimaryEmpty && (
                        <>
                          <AddCircleOutlineIcon sx={{ fontSize: 28, color: 'text.secondary', mb: 0.5 }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Add Widget
                          </Typography>
                        </>
                      )}
                    </SlotPlaceholder>,
                  );
                }
              }

              return cells;
            })}

            {/* Delete-row button — bottom-center of the empty row.
                Hidden in preview mode and when the row is the auto-generated trailing row. */}
            {!readOnly && isGroupEmpty && totalHeight > 1 && !(primaryRow === trailingRowIdx && (sectionAtMaxRow !== null || multiRowWidgetAtMaxRow !== null || isRowFull(items, maxRow, blockCols))) && (
              <DeleteRowButton
                size="small"
                onClick={() => deleteEmptyRow(blockId, primaryRow)}
                onMouseDown={e => e.stopPropagation()}
                onMouseEnter={() => setDeleteHoverRow(primaryRow)}
                onMouseLeave={() => setDeleteHoverRow(null)}
                aria-label="Remove empty row"
                title="Remove row"
              >
                <RemoveCircleOutlineIcon sx={{ fontSize: 22 }} />
              </DeleteRowButton>
            )}
          </SlotGrid>
        );
      })}

      {/* ─── Widget actions menu ─── */}
      {!readOnly && (
        <ThemedMenu
          id={widgetMenuId}
          anchorEl={widgetMenu?.anchorEl}
          open={Boolean(widgetMenu)}
          onClose={() => setWidgetMenu(null)}
          MenuListProps={{ dense: true }}
        >
          <MenuItem onClick={() => widgetMenu && handleConfigure(widgetMenu.row, widgetMenu.slot)}>
            Edit widget
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!widgetMenu) return;
              removeWidgetAtSlot(blockId, widgetMenu.row, widgetMenu.slot);
              setWidgetMenu(null);
            }}
          >
            Remove widget
          </MenuItem>
          {widgetMenuBw && widgetMenuBw.span < (4 - widgetMenuBw.slot) && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetSpan(blockId, widgetMenu.row, widgetMenu.slot, (widgetMenuBw.span + 1) as Slot);
                setWidgetMenu(null);
              }}
            >
              Expand →
            </MenuItem>
          )}
          {widgetMenuBw && widgetMenuBw.span > 1 && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetSpan(blockId, widgetMenu.row, widgetMenu.slot, (widgetMenuBw.span - 1) as Slot);
                setWidgetMenu(null);
              }}
            >
              ← Shrink
            </MenuItem>
          )}
          {widgetMenuBw && (widgetMenuBw.rowSpan ?? 1) < 3 && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetRowSpan(blockId, widgetMenu.row, widgetMenu.slot, ((widgetMenuBw.rowSpan ?? 1) + 1) as Slot);
                setWidgetMenu(null);
              }}
            >
              Expand ↓
            </MenuItem>
          )}
          {widgetMenuBw && (widgetMenuBw.rowSpan ?? 1) > 1 && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetRowSpan(blockId, widgetMenu.row, widgetMenu.slot, ((widgetMenuBw.rowSpan ?? 1) - 1) as Slot);
                setWidgetMenu(null);
              }}
            >
              ↑ Shrink
            </MenuItem>
          )}
        </ThemedMenu>
      )}

      {/* ─── Widget palette (row + slot targeted) ─── */}
      {!readOnly && (
        <WidgetPalette
          open={paletteTarget !== null}
          onClose={() => setPaletteTarget(null)}
          onSelect={type => {
            if (!paletteTarget) return;
            if (type === 'section') {
              addSection(blockId, paletteTarget.row, paletteTarget.slot, 1);
              setPaletteTarget(null);
            } else {
              setPendingSlot({ blockId, row: paletteTarget.row, slot: paletteTarget.slot as 1 | 2 | 3 });
              setPendingWidgetType(type);
              setSidebarPanel('create-widget');
              setPaletteTarget(null);
            }
          }}
        />
      )}
    </Shell>
  );
});

// ─── WidgetRowResizeHandle — bottom-edge pointer-capture resize for row span ─────────────────

type WidgetRowResizeHandleProps = {
  rowSpan: number;
  onResize: (newRowSpan: number) => void;
};

/** Absolutely-positioned bottom strip on a widget cell that lets the user drag to change rowSpan. */
function WidgetRowResizeHandle({ rowSpan, onResize }: WidgetRowResizeHandleProps): React.ReactElement {
  const start = useRef<{ y: number; rows: number } | null>(null);
  return (
    <RowResizeEdge
      onPointerDown={e => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = { y: e.clientY, rows: rowSpan };
      }}
      onPointerMove={e => {
        if (!start.current) return;
        const delta = Math.round((e.clientY - start.current.y) / ROW_HEIGHT);
        const newRowSpan = Math.max(1, Math.min(3, start.current.rows + delta));
        onResize(newRowSpan);
      }}
      onPointerUp={e => {
        if (!start.current) return;
        const delta = Math.round((e.clientY - start.current.y) / ROW_HEIGHT);
        const newRowSpan = Math.max(1, Math.min(3, start.current.rows + delta));
        onResize(newRowSpan);
        start.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
    />
  );
}

// ─── WidgetRenderer — resolves data from context and delegates to the registry render fn ─────

type WidgetRendererProps = {
  widgetId: string;
  config: WidgetConfig;
  width: number;
  height: number;
};

function WidgetRenderer({ widgetId, config, width, height }: WidgetRendererProps): React.ReactElement {
  const { registry } = useWidgetRegistry();
  const data = useDashboardData(widgetId);
  const entry = registry.get(config.type);

  if (!entry) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
        Unknown widget type: {config.type}
      </Typography>
    );
  }

  return entry.render({ widgetId, config, data, width, height });
}

// ─── Styled components ────────────────────────────────────────────────────────

const Shell = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Fill the RGL item but leave BLOCK_GAP (12 px) at the bottom as a visual gap
  // between blocks.  The RGL item height always bakes in that gap.
  height: `calc(100% - ${BLOCK_GAP}px)`,
  width: '100%',
  border: `1px solid ${theme.palette.gray.light2}`,
  borderRadius: '12px',
  background: theme.palette.white.main,
  overflow: 'hidden',
}));

const ShellHeader = styled('div', {
  shouldForwardProp: prop => prop !== 'collapsed' && prop !== 'hideTitle',
})<{ collapsed: boolean; hideTitle: boolean }>(({ theme, collapsed, hideTitle }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
  flexShrink: 0,
  ...(hideTitle ? {
    padding: '2px 4px 2px 8px',
    height: '24px',
    backgroundColor: `${theme.palette.primary.main}18`,
    backdropFilter: 'blur(4px)',
    borderBottom: `1px solid ${theme.palette.primary.light1}`,
  } : {
    padding: '14px 8px 8px 12px',
    borderBottom: collapsed ? 'none' : `1px solid ${theme.palette.gray.light2}`,
    minHeight: '40px',
  }),
}));

const TitleInput = styled(InputBase, {
  shouldForwardProp: prop => prop !== 'hideTitle',
})<{ hideTitle: boolean }>(({ theme, hideTitle }) => ({
  flex: 1,
  ...(hideTitle ? {
    marginRight: '26px',
    '& input': {
      padding: 0,
      cursor: 'text',
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 700,
      color: theme.palette.text.primary,
      lineHeight: 1,
    },
  } : {
    borderBottom: `2px solid ${theme.palette.text.disabled}`,
    '& input': {
      padding: 0,
      cursor: 'text',
      fontSize: theme.typography.h6.fontSize,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
  }),
}));

const SlotGrid = styled('div', {
  shouldForwardProp: prop => prop !== 'flexSpan' && prop !== 'rowCount' && prop !== 'cols' && prop !== 'deleteHovering' && prop !== 'readOnly',
})<{ flexSpan: number; rowCount: number; cols: number; deleteHovering?: boolean; readOnly?: boolean }>(({ theme, flexSpan, rowCount, cols, deleteHovering, readOnly }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `repeat(${cols}, 1fr)`,
  // Track height = ROW_HEIGHT minus top+bottom padding so items fill their
  // track exactly without overflowing the padded container.
  gridTemplateRows: `repeat(${rowCount}, ${ROW_HEIGHT - 2 * SLOT_PADDING}px)`,
  height: `${flexSpan * ROW_HEIGHT}px`,
  flexShrink: 0,
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: deleteHovering ? `${theme.palette.primary.main}0E` : 'transparent',
  transition: 'background-color 150ms',
  '& + &': {
    paddingTop: 0,
    ...(!readOnly && {
      borderTop: `1px dashed ${theme.palette.divider}`,
    }),
  },
}));

const WidgetSlotCell = styled('div', {
  shouldForwardProp: prop => prop !== 'span' && prop !== 'isDragOver' && prop !== 'readOnly' && prop !== 'isEditing' && prop !== 'noBorder',
})<{ span: number; isDragOver: boolean; readOnly?: boolean; isEditing?: boolean; noBorder?: boolean }>(({ theme, isDragOver, isEditing, noBorder }) => ({
  // gridColumn and gridRow are set via inline style for precise sub-row placement.
  minHeight: `${ROW_HEIGHT - 2 * SLOT_PADDING}px`,
  display: 'flex',
  flexDirection: 'column',
  border: noBorder
    ? 'none'
    : isEditing
      ? `2px solid ${theme.palette.primary.main}`
      : `1px solid ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: noBorder ? 0 : '12px',
  background: theme.palette.background.paper,
  transition: 'border-color 150ms',
  position: 'relative',
  ...(isEditing && {
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}30`,
  }),
}));

/** Container cell for a section item — gridColumn/gridRow set via inline style for sub-row placement */
const SectionSlotCell = styled('div', {
  shouldForwardProp: prop => prop !== 'span',
})<{ span: number }>(() => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  position: 'relative',
}));

/**
 * Invisible full-height right-edge zone on a section cell. Shows an ew-resize cursor on hover.
 * Drag it to a further slot to expand the section's column span in the parent block.
 */
const SectionResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '10px',
  height: '100%',
  cursor: 'ew-resize',
  zIndex: 10,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset -3px 0 0 0 ${theme.palette.primary.main}`,
  },
}));

const SlotWidgetHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0.75),
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: '28px',
  flexShrink: 0,
}));

const SlotContent = styled('div')({
  flex: 1,
  position: 'relative',
});

/**
 * Invisible full-height right-edge zone. Shows an ew-resize cursor on hover
 * and a subtle blue highlight so users know they can drag to resize the widget.
 */
const ResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '10px',
  height: '100%',
  cursor: 'ew-resize',
  zIndex: 2,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset -3px 0 0 0 ${theme.palette.primary.main}`,
  },
}));

/**
 * Invisible full-width bottom-edge zone. Shows an s-resize cursor on hover
 * and a subtle blue highlight so users know they can drag to resize the widget vertically.
 */
const RowResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '10px',
  cursor: 's-resize',
  zIndex: 2,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset 0 -3px 0 0 ${theme.palette.primary.main}`,
  },
}));

// Circled-minus button anchored to the bottom-center edge of an empty SlotGrid row
const DeleteRowButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: -7,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 3,
  padding: 0,
  // White backing so it looks like it sits on the border
  backgroundColor: theme.palette.white?.main ?? '#fff',
  borderRadius: '50%',
  color: theme.palette.primary.main,
  transition: 'color 150ms',
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.white?.main ?? '#fff',
  },
}));

const SlotPlaceholder = styled('div', {
  shouldForwardProp: prop => prop !== 'isDragOver' && prop !== 'isPrimary' && prop !== 'isPendingTarget',
})<{ isDragOver: boolean; isPrimary: boolean; isPendingTarget?: boolean }>(({ theme, isDragOver, isPrimary, isPendingTarget }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  // No fixed height — let the CSS grid track determine height (same as WidgetSlotCell).
  minHeight: `${ROW_HEIGHT - 2 * SLOT_PADDING}px`,
  border: isPendingTarget
    ? `2px solid ${theme.palette.primary.main}`
    : `1.5px ${isPrimary ? 'solid' : 'dashed'} ${isDragOver ? theme.palette.primary.main : theme.palette.gray.light2}`,
  borderRadius: '12px',
  cursor: isPrimary ? 'pointer' : 'default',
  transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms',
  backgroundColor: isDragOver ? theme.palette.primary.light4 : 'transparent',
  ...(isPendingTarget && {
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}30`,
  }),
  ...(isPrimary && !isPendingTarget && {
    '&:hover': {
      borderColor: theme.palette.gray.light1,
      backgroundColor: theme.palette.gray.light4,
    },
  }),
}));

/** Clean block-title header rendered in preview mode (no border-bottom, no editing chrome). */
const PreviewBlockHeader = styled('div')(({ theme }) => ({
  padding: '10px 16px 6px',
  flexShrink: 0,
}));

